import { useQueryClient } from '@tanstack/react-query';
import { usePrefs } from '../../lib/prefs';
import { qk } from '../../lib/queries';
import type { PlayInfo } from '@crowdset/contracts';
import { BRAND } from '@crowdset/contracts/constants';
import {
  BookmarkSimple,
  Headphones,
  ShieldCheck,
  SkipForward,
  SignIn,
  ThumbsDown,
  ThumbsUp,
  UserMinus,
  X,
} from '@phosphor-icons/react';
import { Tooltip } from 'radix-ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSession } from '../../lib/queries';
import { clock } from '../../room/clock';
import { useCommand } from '../../room/context';
import { useRoomStore } from '../../room/store';
import { Button, cx, formatDuration } from '../../ui/kit';
import { useRoomDialogs } from './dialogs-store';

function Tip({ label, children }: { label: string | null; children: ReactNode }) {
  if (!label) return <>{children}</>;
  return (
    <Tooltip.Root delayDuration={250}>
      <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
      <Tooltip.Portal>
        <Tooltip.Content
          sideOffset={6}
          className="z-50 max-w-60 rounded-lg border border-white/10 bg-ink-800 px-2.5 py-1.5 text-xs text-fg shadow-lg"
        >
          {label}
        </Tooltip.Content>
      </Tooltip.Portal>
    </Tooltip.Root>
  );
}

export function useRoomProgress(play: PlayInfo | null) {
  const [now, setNow] = useState(() => clock.serverNow());
  useEffect(() => {
    if (!play) return;
    const t = setInterval(() => setNow(clock.serverNow()), 500);
    return () => clearInterval(t);
  }, [play]);
  if (!play) return { elapsed: 0, duration: 0, pct: 0, early: false };
  const raw = now - play.startsAtMs;
  const elapsed = Math.min(Math.max(raw, 0), play.durationMs);
  return {
    elapsed,
    duration: play.durationMs,
    pct: (elapsed / play.durationMs) * 100,
    early: raw < 0,
  };
}

export function NowPlaying({ compact = false }: { compact?: boolean }) {
  const play = useRoomStore((s) => s.data?.play ?? null);
  const { elapsed, duration, pct, early } = useRoomProgress(play);
  if (!play) {
    return (
      <div className="min-w-0">
        <p className="text-sm font-medium">Nothing playing</p>
        <p className="text-xs text-fg-muted">The room is idle until someone joins the DJ queue.</p>
      </div>
    );
  }
  return (
    <div
      className="min-w-0 flex-1 lg:min-w-[220px] lg:basis-64"
      aria-live="polite"
      aria-atomic="true"
      data-testid="now-playing"
      data-play-id={play.playId}
    >
      <p
        className={cx('truncate font-semibold tracking-tight', compact ? 'text-sm' : 'text-[15px]')}
        title={play.title}
      >
        {play.title}
      </p>
      <p className="truncate text-xs text-fg-muted">
        {play.channelTitle} <span className="text-fg-subtle">· played by</span>{' '}
        <span className="text-fg" data-testid="now-playing-dj">
          {play.dj.displayName}
        </span>
      </p>
      <div
        className="mt-1.5 flex items-center gap-2 font-mono text-[11px] text-fg-subtle"
        aria-hidden="true"
      >
        <span>{early ? 'starting' : formatDuration(elapsed)}</span>
        <div className="h-1 flex-1 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-accent/80 transition-[width] duration-500 ease-linear"
            style={{ width: `${pct}%` }}
          />
        </div>
        <span>{formatDuration(duration)}</span>
      </div>
    </div>
  );
}

export function RoomActions({ layout = 'bar' }: { layout?: 'bar' | 'stack' }) {
  const data = useRoomStore((s) => s.data)!;
  const session = useSession().data!;
  const nav = useNavigate();
  const loc = useLocation();
  const { me, play, reactions } = data;
  const react = useCommand('reaction.set');
  const djJoin = useCommand('dj.join', { success: 'You joined the DJ queue.' });
  // Joining the queue is a user gesture: treat it as consent to play, so your own turn is audible.
  const joinQueue = () => {
    usePrefs.getState().setPlaybackConsent(true);
    void djJoin.run({});
  };
  const djLeave = useCommand('dj.leave');
  const save = useCommand('track.save');
  const qc = useQueryClient();
  const removeDj = useCommand('moderation.removeDj', {
    success: 'DJ removed from the rotation. Their current track keeps playing.',
  });
  const openDialog = useRoomDialogs((s) => s.open);
  const myVote = me.userId ? reactions.votes[me.userId] : undefined;
  const signedIn = !!session.user;
  const isMod = me.role === 'moderator' || me.role === 'owner';
  const hasPlay = useRoomStore((s) => !!s.data?.play);
  const position = me.userId ? data.queue.findIndex((q) => q.user.id === me.userId) : -1;

  const voteBlock = !signedIn
    ? 'Sign in to react'
    : !play
      ? 'Nothing is playing'
      : me.isCurrentDj
        ? 'You cannot vote on your own track'
        : !me.canVote
          ? 'Voting is not available to you right now'
          : null;

  const vote = (v: 'woot' | 'meh') => {
    if (!play) return;
    void react.run({ playId: play.playId, value: myVote === v ? 'none' : v });
  };

  const onSave = async () => {
    if (!play) return;
    const ack = await save.run({ playId: play.playId });
    if (ack.ok) {
      // Saving may have created/activated a "Saved" playlist: refresh library views now.
      void qc.invalidateQueries({ queryKey: qk.playlists });
      void qc.invalidateQueries({ queryKey: qk.playlist(ack.data.playlistId) });
      void qc.invalidateQueries({ queryKey: qk.session });
    }
    if (ack.ok)
      useRoomStore
        .getState()
        .toast(
          ack.data.alreadySaved ? 'Already in your playlist.' : 'Saved to your playlist.',
          'ok',
        );
  };

  const signIn = () => nav(`/signin?returnTo=${encodeURIComponent(loc.pathname)}`);

  let queueButton: ReactNode;
  if (!signedIn) {
    queueButton = (
      <Button variant="primary" onClick={signIn}>
        <SignIn size={16} weight="bold" aria-hidden /> Sign in to DJ
      </Button>
    );
  } else if (me.isCurrentDj) {
    queueButton = me.continuesAfterTurn ? (
      <Button variant="secondary" onClick={() => void djLeave.run({})} loading={djLeave.pending}>
        <X size={16} weight="bold" aria-hidden /> Leave after this track
      </Button>
    ) : (
      <Button variant="primary" onClick={joinQueue} loading={djJoin.pending}>
        <Headphones size={16} weight="bold" aria-hidden /> Stay in rotation
      </Button>
    );
  } else if (me.inQueue) {
    queueButton = (
      <Button variant="secondary" onClick={() => void djLeave.run({})} loading={djLeave.pending}>
        <X size={16} weight="bold" aria-hidden /> Leave queue
        {position >= 0 && <span className="font-mono text-xs text-fg-subtle">#{position + 1}</span>}
      </Button>
    );
  } else {
    queueButton = (
      <Button variant="primary" onClick={joinQueue} loading={djJoin.pending}>
        <Headphones size={16} weight="bold" aria-hidden /> Join DJ queue
      </Button>
    );
  }

  return (
    <Tooltip.Provider>
      <div
        className={cx('flex flex-wrap items-center gap-2', layout === 'stack' && 'justify-between')}
      >
        <div className="flex items-center gap-1.5" role="group" aria-label={BRAND.reactionLabel}>
          <Tip label={voteBlock}>
            <span>
              <button
                onClick={() => vote('woot')}
                disabled={!!voteBlock || react.pending}
                aria-pressed={myVote === 'woot'}
                aria-label={`Woot (${reactions.woots})`}
                className={cx(
                  'flex h-10 items-center gap-2 rounded-xl border px-3.5 text-sm font-medium transition-[background-color,transform,color] duration-200 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45',
                  myVote === 'woot'
                    ? 'border-woot/50 bg-woot/20 text-woot'
                    : 'border-white/[0.08] bg-ink-800 text-fg hover:border-woot/40 hover:text-woot',
                )}
              >
                <ThumbsUp size={17} weight={myVote === 'woot' ? 'fill' : 'bold'} aria-hidden />
                Woot
                <span className="font-mono text-xs opacity-80">{reactions.woots}</span>
              </button>
            </span>
          </Tip>
          <Tip label={voteBlock}>
            <span>
              <button
                onClick={() => vote('meh')}
                disabled={!!voteBlock || react.pending}
                aria-pressed={myVote === 'meh'}
                aria-label={`Meh (${reactions.mehs})`}
                className={cx(
                  'flex h-10 items-center gap-2 rounded-xl border px-3.5 text-sm font-medium transition-[background-color,transform,color] duration-200 active:scale-[0.97] disabled:cursor-not-allowed disabled:opacity-45',
                  myVote === 'meh'
                    ? 'border-meh/50 bg-meh/20 text-meh'
                    : 'border-white/[0.08] bg-ink-800 text-fg hover:border-meh/40 hover:text-meh',
                )}
              >
                <ThumbsDown size={17} weight={myVote === 'meh' ? 'fill' : 'bold'} aria-hidden />
                Meh
                <span className="font-mono text-xs opacity-80">{reactions.mehs}</span>
              </button>
            </span>
          </Tip>
          <Tip
            label={
              signedIn
                ? play
                  ? 'Save this track to your active playlist'
                  : 'Nothing is playing'
                : 'Sign in to save tracks'
            }
          >
            <span>
              <Button
                variant="secondary"
                onClick={() => void onSave()}
                disabled={!signedIn || !play}
                loading={save.pending}
                aria-label="Save track"
              >
                <BookmarkSimple size={17} weight="bold" aria-hidden />
                <span className="hidden sm:inline">Save</span>
              </Button>
            </span>
          </Tip>
        </div>
        <div className="flex items-center gap-2">{queueButton}</div>
        {isMod && hasPlay && (
          <div
            className="flex items-center gap-1.5 rounded-xl border border-dashed border-warn/35 px-1.5 py-1"
            role="group"
            aria-label="Room-wide moderation (affects everyone)"
          >
            <ShieldCheck size={15} weight="bold" className="ml-1 text-warn/80" aria-hidden />
            <span className="hidden text-[11px] font-medium uppercase tracking-wider text-warn/80 2xl:inline">
              Room-wide
            </span>
            <Button
              size="sm"
              variant="ghost"
              className="text-[#f0c9a0]"
              disabled={!play}
              onClick={() => openDialog({ kind: 'skip' })}
              aria-label="Skip track for everyone…"
              title="Skip track for everyone"
            >
              <SkipForward size={14} weight="bold" aria-hidden />{' '}
              <span className="hidden 2xl:inline">Skip…</span>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-[#f0c9a0]"
              disabled={!play || play.dj.id === me.userId}
              onClick={() => play && void removeDj.run({ userId: play.dj.id, reason: '' })}
              title="Remove the current DJ from rotation. Their current track keeps playing."
              aria-label="Remove current DJ from rotation"
            >
              <UserMinus size={14} weight="bold" aria-hidden />{' '}
              <span className="hidden 2xl:inline">Remove DJ</span>
            </Button>
          </div>
        )}
      </div>
    </Tooltip.Provider>
  );
}
